import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkGrid from "@/components/feature/WorkGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { filterWorks, getPopular } from "@/utils/work";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { WorkType } from "@/mocks/movies";

const TYPE_TABS: { label: string; value: WorkType | "all" }[] = [
  { label: "全部", value: "all" },
  { label: "电影", value: "movie" },
  { label: "电视剧", value: "tvseries" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const type = (searchParams.get("type") as WorkType | "all") || "all";
  const sort = searchParams.get("sort") || "popularity";

  const [input, setInput] = useState(keyword);
  const [loading, setLoading] = useState(false);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 | 韩国伦理在线观看` : "搜索影视 | 韩国伦理在线观看",
    "输入韩国伦理电影或电视剧的名称、导演、演员进行搜索，快速找到想看的伦理影视作品。",
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    return filterWorks({ query: keyword, type, sort });
  }, [keyword, type, sort]);

  useEffect(() => {
    if (!keyword.trim()) return undefined;
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 380);
    return () => window.clearTimeout(timer);
  }, [keyword, type, sort]);

  const hotWorks = getPopular(5);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) {
      next.set("q", input.trim());
    } else {
      next.delete("q");
    }
    next.delete("page");
    setSearchParams(next);
  };

  const updateParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索影视" }]} />
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            搜索韩国伦理影视 - 韩国伦理在线观看
          </h1>

          <form
            onSubmit={submitSearch}
            className="mt-5 flex w-full max-w-2xl flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入片名、导演或演员，例如：首尔夜雨"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line text-base" />
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => updateParam("type", tab.value, "all")}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                    type === tab.value
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <select
              value={sort}
              onChange={(event) => updateParam("sort", event.target.value, "popularity")}
              className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 outline-none focus:border-primary-400"
            >
              <option value="popularity">按热度排序</option>
              <option value="latest">按最新排序</option>
              <option value="rating">按评分排序</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-8">
        {keyword.trim() ? (
          <>
            <p className="mb-5 text-sm text-foreground-600">
              关键词「<span className="font-bold text-foreground-950">{keyword}</span>
              」共找到 <span className="font-bold text-accent-600">{results.length}</span> 个结果
            </p>
            <WorkGrid
              works={results}
              loading={loading}
              skeletonCount={6}
              columns={4}
              emptyTitle="未找到相关影视"
              emptyDescription="换一个关键词试试吧，也可以看看下面的热门推荐。"
            />
            {!loading && results.length === 0 && (
              <div className="mt-12">
                <SectionHeading eyebrow="热门推荐" title="或许你会喜欢" />
                <WorkGrid works={hotWorks} columns={5} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                <i className="ri-search-eye-line" />
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">
                输入关键词开始搜索
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                支持按片名、导演、演员与题材标签搜索韩国伦理影视作品。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {["首尔夜雨", "家庭伦理", "伦理悬疑", "都市伦理"].map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => {
                      setInput(word);
                      const next = new URLSearchParams(searchParams);
                      next.set("q", word);
                      setSearchParams(next);
                    }}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <SectionHeading
                eyebrow="大家都在搜"
                title="热门韩国伦理影视"
                linkTo="/category/all"
                linkLabel="浏览全部"
              />
              <WorkGrid works={getPopular(10)} columns={5} />
            </div>
          </>
        )}
      </div>
    </SiteLayout>
  );
}